import { AlertCircle } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"

/**
 * OKS-637 — Per-mission "Action required" badge.
 *
 * A small amber attention pill rendered in a mission card's content header
 * (title row), marking a collab that is "ready to submit" — i.e. whose id is in
 * the page's `ready_to_submit_ids` set (the same OKS-484 signal that drives the
 * Missions-tab "N to submit" count). It is the per-card anchor for that tab
 * count: the number of these badges on the list reconciles with the tab total
 * (both read one id set — see TRD KD-1).
 *
 * Rendered on BOTH `MissionCard` and `PromotionalMissionCard` (TRD KD-2): a
 * promotional ready-to-submit collab renders as the promo card, so badging only
 * `MissionCard` would drop it below the tab count and break reconciliation.
 *
 * Design notes:
 *   - NOT colour-only — pairs an `AlertCircle` icon + text label (a11y,
 *     UX Spec §6). The icon is decorative (`aria-hidden`); the visible label
 *     carries the meaning, extended by an sr-only clause for context.
 *   - `pointer-events-none` so the marker never intercepts a tap from the host
 *     card's stretched whole-card link (UX Spec §5). It is a non-interactive,
 *     non-focusable `<span>` (shadcn `Badge` default element).
 *   - Placed in normal document flow (title row), never a cover overlay — the
 *     card's overlay corners are welded to status/decline/social-proof
 *     affordances (UX Spec §2).
 */
export function ActionRequiredBadge({ className }: { className?: string }) {
  const { t } = useTranslation()

  return (
    <Badge
      data-testid="mission-card-action-required-badge"
      className={cn(
        "pointer-events-none shrink-0 gap-1 border-transparent bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-100",
        className,
      )}
    >
      <AlertCircle aria-hidden="true" />
      <span>{t("missions.card.action_required")}</span>
      <span className="sr-only">
        {" — "}
        {t("missions.card.action_required_aria")}
      </span>
    </Badge>
  )
}
